import { ThemeEvents } from '@theme/events';

/** @typedef {import('@theme/events').CartAddEvent} CartAddEvent */

const SKIP_SOURCES = new Set(['cart-items-component', 'cart-prize-bonus', 'cart-delivery']);
const AUTO_CLOSE_MS = 8000;

function getTheme() {
  return /** @type {any} */ (window.Theme) || /** @type {any} */ (globalThis.Theme);
}

/**
 * @returns {{ type: string, addAction: string, notificationPosition: string }}
 */
function getCartSettings() {
  return getTheme()?.cart || {
    type: 'page',
    addAction: 'notification',
    notificationPosition: 'center',
  };
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isAddEvent(event) {
  const data = /** @type {any} */ (event).detail?.data;
  if (!data || data.didError) return false;
  if (data.skipCartAction) return false;
  if (SKIP_SOURCES.has(data.source)) return false;
  return true;
}

/**
 * @returns {'notification' | 'open_drawer' | 'go_to_cart'}
 */
function resolveAction() {
  const { type, addAction } = getCartSettings();
  const action = addAction || 'notification';

  if (action === 'open_drawer') {
    if (type === 'drawer') return 'open_drawer';
    if (type === 'page') return 'go_to_cart';
  }

  return 'notification';
}

/**
 * @param {number} cents
 * @returns {string}
 */
function formatCartMoney(cents) {
  const theme = getTheme();
  if (typeof theme?.formatMoney === 'function') {
    return theme.formatMoney(cents);
  }

  if (typeof window.Shopify?.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents, theme?.moneyFormat);
  }

  const value = (Number(cents) || 0) / 100;
  const currency = theme?.currency || 'BRL';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    return value.toFixed(2);
  }
}

/**
 * @param {Event} event
 * @returns {Promise<any | null>}
 */
async function resolveLineItem(event) {
  const detail = /** @type {any} */ (event).detail;
  const data = detail?.data;

  if (data?.items?.length) {
    return data.items[data.items.length - 1];
  }

  if (detail?.resource?.items?.length) {
    const items = detail.resource.items;
    return items[items.length - 1];
  }

  const variantId = data?.variantId;
  try {
    const response = await fetch(`${getTheme()?.routes?.cart_url || '/cart'}.js`);
    if (!response.ok) return null;
    const cart = await response.json();
    if (!cart?.items?.length) return null;

    if (variantId) {
      const match = cart.items.find((item) => String(item.variant_id) === String(variantId));
      if (match) return match;
    }

    return cart.items[cart.items.length - 1];
  } catch {
    return null;
  }
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  if (typeof drawer.open === 'function') {
    drawer.open();
    return;
  }

  drawer.showDialog?.();
}

function goToCartPage() {
  window.location.href = getTheme()?.routes?.cart_url || '/cart';
}

class CartAddNotificationComponent extends HTMLElement {
  /** @type {number | undefined} */
  #autoCloseTimer;

  connectedCallback() {
    this.querySelector('[data-cart-add-notification-close]')?.addEventListener('click', () => this.hide());
    document.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeyDown);
    this.#clearAutoClose();
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!this.classList.contains('is-visible')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.hide();
    }
  };

  #clearAutoClose() {
    if (this.#autoCloseTimer) {
      clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = undefined;
    }
  }

  /**
   * @param {any} item
   */
  show(item) {
    if (!item) return;

    const titleEl = this.querySelector('[data-cart-add-notification-title]');
    const variantEl = this.querySelector('[data-cart-add-notification-variant]');
    const priceEl = this.querySelector('[data-cart-add-notification-price]');
    const imageEl = /** @type {HTMLImageElement | null} */ (
      this.querySelector('[data-cart-add-notification-image]')
    );

    const productTitle = item.product_title || item.title || '';
    const variantTitle = item.variant_title;
    const showVariant = variantTitle && variantTitle !== 'Default Title' && variantTitle !== productTitle;

    if (titleEl) titleEl.textContent = productTitle;

    if (variantEl instanceof HTMLElement) {
      variantEl.textContent = showVariant ? variantTitle : '';
      variantEl.hidden = !showVariant;
    }

    if (priceEl) {
      const unitPrice = item.final_price ?? item.price;
      priceEl.textContent = formatCartMoney(unitPrice);
    }

    const imageUrl = item.featured_image?.url || item.image || item.featured_image;
    if (imageEl) {
      if (imageUrl) {
        imageEl.src = imageUrl;
        imageEl.alt = productTitle;
        imageEl.hidden = false;
      } else {
        imageEl.removeAttribute('src');
        imageEl.alt = '';
        imageEl.hidden = true;
      }
    }

    this.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('is-visible');
    });

    this.#clearAutoClose();
    this.#autoCloseTimer = window.setTimeout(() => this.hide(), AUTO_CLOSE_MS);

    this.querySelector('[data-cart-add-notification-close]')?.focus?.();
  }

  hide() {
    this.#clearAutoClose();
    this.classList.remove('is-visible');
    this.hidden = true;
  }
}

if (!customElements.get('cart-add-notification-component')) {
  customElements.define('cart-add-notification-component', CartAddNotificationComponent);
}

/**
 * @param {Event} event
 */
async function handleCartAddAction(event) {
  if (!isAddEvent(event)) return;

  const action = resolveAction();

  if (action === 'open_drawer') {
    openCartDrawer();
    return;
  }

  if (action === 'go_to_cart') {
    goToCartPage();
    return;
  }

  const item = await resolveLineItem(event);
  const notification = document.querySelector('cart-add-notification-component');
  if (notification instanceof CartAddNotificationComponent && item) {
    notification.show(item);
  }
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartAddAction);
